import React, { useCallback, useEffect, useMemo, useState, useRef, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, LinkIcon, GlobeIcon, BookOpenIcon, SparklesIcon, CheckIcon, XIcon, FileTextIcon, AlertCircleIcon, MicIcon, SquareIcon, InfoIcon, FileIcon, ImageIcon, PresentationIcon, ShieldCheckIcon, ArrowRightIcon, CheckCircleIcon, ClipboardListIcon, SearchIcon, RouteIcon, Loader2Icon, YoutubeIcon, MonitorIcon, SmartphoneIcon, ExternalLinkIcon, PencilIcon, ClockIcon, GraduationCapIcon, ChevronDownIcon, LockIcon, EyeIcon } from 'lucide-react';
import { AssessmentSettings } from './AssessmentSettings';
import { AssessmentWorkspace } from './AssessmentWorkspace';
import type { Visibility, Subject, Tag, EducationGroup } from './AssessmentSettings';
import { WebSearchModal, type WebSearchResult } from './WebSearchModal';
import { JourneyPlanningScreen } from './JourneyPlanningScreen';
type SourceType = 'upload' | 'link' | 'search' | 'questionbank' | 'text' | null;
type WizardStep = 'source' | 'instructions';
type CreationMode = 'assessment' | 'journey' | null;
// Real audio-reactive waveform using frequency data from AnalyserNode
const LiveWaveform = ({
  analyserRef
}: {
  analyserRef: React.RefObject<AnalyserNode | null>;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    analyser.fftSize = 128;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const barCount = 40;
      const gap = 2;
      const totalGap = (barCount - 1) * gap;
      const barWidth = Math.max(1, (rect.width - totalGap) / barCount);
      const centerY = rect.height / 2;
      if (rect.width < 10 || rect.height < 4) return;
      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor(i / barCount * bufferLength);
        const value = dataArray[dataIndex] / 255;
        const center = barCount / 2;
        const distFromCenter = Math.abs(i - center) / center;
        const centerWeight = 1 - distFromCenter * 0.4;
        const barHeight = Math.max(2, value * centerWeight * (rect.height * 0.8));
        const x = i * (barWidth + gap);
        const y = centerY - barHeight / 2;
        const alpha = 0.2 + value * 0.4;
        const radius = Math.max(0, barWidth / 2);
        ctx.fillStyle = `rgba(156, 163, 175, ${alpha})`;
        ctx.beginPath();
        ctx.roundRect(x, y, Math.max(0, barWidth), Math.max(0, barHeight), radius);
        ctx.fill();
      }
    };
    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [analyserRef]);
  return <canvas ref={canvasRef} className="w-full h-6" style={{
    display: 'block'
  }} data-id="element-1040" />;
};
const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};
const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return FileTextIcon;
  if (['doc', 'docx'].includes(ext || '')) return FileIcon;
  if (['ppt', 'pptx'].includes(ext || '')) return PresentationIcon;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return ImageIcon;
  return FileIcon;
};
const getFileTypeLabel = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toUpperCase();
  return ext || 'FILE';
};
const assessmentTemplates = [{
  id: 'quick-check',
  name: 'Quick Check',
  shortName: 'Quick',
  tooltip: 'A short diagnostic to quickly assess understanding of a specific concept. Best for formative checks during or after a lesson.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 5–8 question MCQ assessment based on the attached context or the topic above.\n\nFocus: High-level recall of core concepts.\n\nDifficulty: Foundational / Uniform.\n\nConstraint: Questions must be concise for a rapid (<10 min) check.`,
  cursorLine: 1
}, {
  id: 'practice-set',
  name: 'Practice Set',
  shortName: 'Practice',
  tooltip: 'A focused set of practice questions designed to build mastery through application and progressive difficulty.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 10–15 question MCQ practice set based on the attached context or the topic above.\n\nFocus: Mastering atomic units (specific skills).\n\nDifficulty: Gradual increase. Sequence from simple facts to multi-step application.\n\nConstraint: Use high-quality distractors targeting common misconceptions.`,
  cursorLine: 1
}, {
  id: 'unit-assessment',
  name: 'Unit Assessment',
  shortName: 'Unit',
  tooltip: 'A comprehensive evaluation covering an entire chapter or unit. Suitable for graded or summative assessment.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 16–30 question MCQ assessment based on the attached context or the topic above.\n\nFocus: Full topic hierarchy and synthesis.\n\nDifficulty: Tiered progression. Organize into levels — Fundamentals, Application, and Synthesis.\n\nConstraint: Use high-quality distractors targeting common misconceptions.`,
  cursorLine: 1
}];
const journeyTemplates = [{
  id: 'presentation',
  name: 'Presentation',
  shortName: 'Present',
  tooltip: 'Optimized for classroom presentations with desktop-friendly card dimensions. Perfect for teaching sessions.',
  prompt: `Journey Type: Presentation (Classroom)\nTopic / Chapter: \nCurriculum / Exam: \nGrade / Level: \n\nCreate a learning journey optimized for classroom presentation (5–10 topics). Design each topic card for desktop/projector viewing with clear visuals, concise explanations, and interactive discussion points. Include engaging examples and checkpoints suitable for live teaching. Focus on visual clarity and easy navigation for instructor-led sessions.`,
  cursorLine: 1,
  cardOptimization: 'desktop' as const
}, {
  id: 'assignment',
  name: 'Assignment',
  shortName: 'Assign',
  tooltip: 'Optimized for homework with mobile-friendly card dimensions. Perfect for independent student learning.',
  prompt: `Journey Type: Assignment (Homework)\nTopic / Chapter: \nCurriculum / Exam: \nGrade / Level: \n\nCreate a learning journey optimized for independent study (5–10 topics). Design each topic card for mobile viewing with self-paced explanations, practice activities, and clear instructions. Include progress checkpoints and reflection prompts. Focus on mobile-friendly layouts that work well for students learning at home on their devices.`,
  cursorLine: 1,
  cardOptimization: 'mobile' as const
}];
// Demo words for recording simulation (hoisted to avoid re-creation)
const demoWords = ['Create', 'a', 'quiz', 'on', 'fractions', 'for', 'grade', '5', 'students', 'covering', 'addition', 'and', 'subtraction', 'of', 'fractions', 'with', 'unlike', 'denominators'];
// Processing stage labels (shared between useEffect and status bar)
const processingStagesWithSource = ['Uploading source...', 'Analyzing content...', 'Indexing & chunking...', 'Ready!'];
const processingStagesWithoutSource = ['Analyzing instructions...', 'Preparing generation...', 'Setting up...', 'Ready!'];
// Source icon resolver (used in instructions chips and processing modal)
const getSourceIconComponent = (type: 'file' | 'link' | 'text' | 'web') => {
  switch (type) {
    case 'file':
      return FileTextIcon;
    case 'link':
      return LinkIcon;
    case 'text':
      return ClipboardListIcon;
    default:
      return GlobeIcon;
  }
};
const getSourceIconColor = (type: 'file' | 'link' | 'text' | 'web') => {
  switch (type) {
    case 'file':
      return 'text-red-500';
    case 'link':
      return 'text-[#2563EB]';
    case 'text':
      return 'text-[#8B5CF6]';
    case 'web':
      return 'text-[#FF9D33]';
    default:
      return 'text-text-tertiary';
  }
};
const CreateAssessmentOptions = () => {
  const [creationMode, setCreationMode] = useState<CreationMode>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [activeSourceTab, setActiveSourceTab] = useState<SourceType>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    name: string;
  }>>([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkConfirmed, setLinkConfirmed] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [copiedTextConfirmed, setCopiedTextConfirmed] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [importedWebSources, setImportedWebSources] = useState<WebSearchResult[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [validationMessage, setValidationMessage] = useState('');
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Indexing state — tracks which sources are currently being indexed
  const [indexingSources, setIndexingSources] = useState<Set<string>>(new Set());
  const indexingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Grade dropdown state for wizard footer
  const [isGradeDropdownOpen, setIsGradeDropdownOpen] = useState(false);
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);
  // Learning Journey card optimization (desktop vs mobile)
  const [cardOptimization, setCardOptimization] = useState<'desktop' | 'mobile'>('desktop');
  // Journey planning screen state
  const [showJourneyPlanning, setShowJourneyPlanning] = useState(false);
  // Architect loading state (assessment mode)
  const [showArchitectLoading, setShowArchitectLoading] = useState(false);
  // Wizard step state
  const [activeStep, setActiveStep] = useState<WizardStep>('source');
  // Processing phase state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Assessment Settings state
  const [title, setTitle] = useState('Untitled');
  const [icon, setIcon] = useState('clipboard');
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>([]);
  const [allSubjects, setAllSubjects] = useState<Subject[]>([{
    id: 'math',
    label: 'Mathematics',
    isSystem: true
  }, {
    id: 'science',
    label: 'Science',
    isSystem: true
  }, {
    id: 'english',
    label: 'English',
    isSystem: true
  }, {
    id: 'history',
    label: 'History',
    isSystem: true
  }, {
    id: 'geography',
    label: 'Geography',
    isSystem: true
  }, {
    id: 'physics',
    label: 'Physics',
    isSystem: true
  }, {
    id: 'chemistry',
    label: 'Chemistry',
    isSystem: true
  }, {
    id: 'biology',
    label: 'Biology',
    isSystem: true
  }, {
    id: 'ict',
    label: 'ICT / Computer Science',
    isSystem: true
  }, {
    id: 'art',
    label: 'Art & Design',
    isSystem: true
  }]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([{
    id: 'quiz',
    label: 'Quiz',
    color: 'blue',
    usageCount: 5
  }, {
    id: 'homework',
    label: 'Homework',
    color: 'green',
    usageCount: 3
  }, {
    id: 'exam',
    label: 'Exam',
    color: 'red',
    usageCount: 8
  }, {
    id: 'practice',
    label: 'Practice',
    color: 'purple',
    usageCount: 2
  }, {
    id: 'revision',
    label: 'Revision',
    color: 'yellow',
    usageCount: 4
  }]);
  const groups: EducationGroup[] = [{
    id: 'grade-1',
    label: 'Grade 1'
  }, {
    id: 'grade-2',
    label: 'Grade 2'
  }, {
    id: 'grade-3',
    label: 'Grade 3'
  }, {
    id: 'grade-4',
    label: 'Grade 4'
  }, {
    id: 'grade-5',
    label: 'Grade 5'
  }, {
    id: 'grade-6',
    label: 'Grade 6'
  }, {
    id: 'grade-7',
    label: 'Grade 7'
  }, {
    id: 'grade-8',
    label: 'Grade 8'
  }, {
    id: 'grade-9',
    label: 'Grade 9'
  }, {
    id: 'grade-10',
    label: 'Grade 10'
  }, {
    id: 'grade-11',
    label: 'Grade 11'
  }, {
    id: 'grade-12',
    label: 'Grade 12'
  }];
  // Grouped grades for multi-step dropdown
  const gradeStages = [{
    id: 'primary',
    label: 'Primary School',
    grades: groups.filter((_, i) => i < 6)
  }, {
    id: 'middle',
    label: 'Middle School',
    grades: groups.filter((_, i) => i >= 6 && i < 9)
  }, {
    id: 'senior',
    label: 'Senior Secondary',
    grades: groups.filter((_, i) => i >= 9)
  }];
  const selectedGroupLabel = groups.find(g => g.id === selectedGroup)?.label;
  // Close grade dropdown on outside click
  useEffect(() => {
    if (!isGradeDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(e.target as Node)) {
        setIsGradeDropdownOpen(false);
        setExpandedStage(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isGradeDropdownOpen]);
  // Audio refs for real waveform
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Determine which templates to use based on mode
  const templates = creationMode === 'journey' ? journeyTemplates : assessmentTemplates;
  const isJourney = creationMode === 'journey';
  // Auto-resize textarea to fit content
  const autoResizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 300;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, []);
  useEffect(() => {
    autoResizeTextarea();
  }, [prompt, autoResizeTextarea]);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  // Simulate indexing for a source
  const simulateIndexing = useCallback((sourceId: string, duration = 2200) => {
    setIndexingSources(prev => new Set([...prev, sourceId]));
    const timer = setTimeout(() => {
      setIndexingSources(prev => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
      indexingTimersRef.current.delete(sourceId);
    }, duration);
    indexingTimersRef.current.set(sourceId, timer);
  }, []);
  const cleanupAudio = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      mediaStreamRef.current = stream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;
      setIsRecording(true);
      setRecordingSeconds(0);
      setLiveTranscript('');
      setActiveStep('instructions');
      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      demoWords.forEach((word, i) => {
        const t = setTimeout(() => {
          setLiveTranscript(prev => prev ? prev + ' ' + word : word);
        }, 600 + i * 280);
        timeouts.push(t);
      });
      transcriptTimeouts.current = timeouts;
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  }, []);
  const stopRecording = useCallback(() => {
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    transcriptTimeouts.current.forEach(clearTimeout);
    transcriptTimeouts.current = [];
    if (liveTranscript.trim()) {
      setPrompt(prev => {
        const separator = prev.trim() ? '\n' : '';
        return prev + separator + liveTranscript;
      });
    }
    setLiveTranscript('');
    setRecordingSeconds(0);
    cleanupAudio();
    setTimeout(() => textareaRef.current?.focus(), 150);
  }, [liveTranscript, cleanupAudio]);
  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    transcriptTimeouts.current.forEach(clearTimeout);
    transcriptTimeouts.current = [];
    setLiveTranscript('');
    setRecordingSeconds(0);
    cleanupAudio();
  }, [cleanupAudio]);
  // Keyboard shortcuts during recording
  useEffect(() => {
    if (!isRecording) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRecording();
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        stopRecording();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isRecording, cancelRecording, stopRecording]);
  // Close wizard modal on Escape
  useEffect(() => {
    if (creationMode === null || isProcessing || isRecording) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleChangeMode();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [creationMode, isProcessing, isRecording]);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
      transcriptTimeouts.current.forEach(clearTimeout);
      indexingTimersRef.current.forEach(timer => clearTimeout(timer));
      indexingTimersRef.current.clear();
      cleanupAudio();
    };
  }, [cleanupAudio]);
  const showValidation = useCallback((msg: string) => {
    setValidationMessage(msg);
    if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    validationTimerRef.current = setTimeout(() => setValidationMessage(''), 4000);
  }, []);
  const MAX_SOURCES = 5;
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  // Unified source count across all types
  const totalSourceCount = uploadedFiles.length + (linkConfirmed ? 1 : 0) + (copiedTextConfirmed ? 1 : 0) + importedWebSources.length;
  const remainingSlots = MAX_SOURCES - totalSourceCount;
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
      e.target.value = '';
      return;
    }
    const validFiles = Array.from(files).filter(f => {
      if (f.size > MAX_FILE_SIZE_BYTES) {
        showValidation(`"${f.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;
    const newFiles = validFiles.map(f => ({
      name: f.name
    }));
    setUploadedFiles(prev => {
      const slotsForFiles = MAX_SOURCES - (prev.length + (linkConfirmed ? 1 : 0) + (copiedTextConfirmed ? 1 : 0) + importedWebSources.length);
      const combined = [...prev, ...newFiles].slice(0, prev.length + slotsForFiles);
      if (newFiles.length > slotsForFiles) {
        showValidation(`Only ${slotsForFiles} slot${slotsForFiles === 1 ? '' : 's'} remaining. Some files were not added.`);
      }
      return combined;
    });
    setActiveSourceTab('upload');
    newFiles.slice(0, remainingSlots).forEach((f, i) => simulateIndexing(`file-${f.name}`, 2500 + i * 300));
    setTimeout(() => setActiveStep('instructions'), 350);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;
    if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
      return;
    }
    const validExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const validFiles = Array.from(droppedFiles).filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      if (!validExtensions.includes(ext)) return false;
      if (f.size > MAX_FILE_SIZE_BYTES) {
        showValidation(`"${f.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
        return false;
      }
      return true;
    }).map(f => ({
      name: f.name
    }));
    if (validFiles.length > 0) {
      setUploadedFiles(prev => {
        const slotsForFiles = MAX_SOURCES - (prev.length + (linkConfirmed ? 1 : 0) + (copiedTextConfirmed ? 1 : 0) + importedWebSources.length);
        if (validFiles.length > slotsForFiles) {
          showValidation(`Only ${slotsForFiles} slot${slotsForFiles === 1 ? '' : 's'} remaining. Some files were not added.`);
        }
        return [...prev, ...validFiles].slice(0, prev.length + slotsForFiles);
      });
      setActiveSourceTab('upload');
      validFiles.slice(0, remainingSlots).forEach((f, i) => simulateIndexing(`file-${f.name}`, 2500 + i * 300));
      setTimeout(() => setActiveStep('instructions'), 350);
    }
  }, [simulateIndexing, remainingSlots, showValidation, linkConfirmed, copiedTextConfirmed, importedWebSources.length]);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleLinkAdd = () => {
    if (linkUrl.trim()) {
      if (remainingSlots <= 0) {
        showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
        return;
      }
      setLinkConfirmed(true);
      simulateIndexing('link', 2000);
      // Auto-advance to instructions
      setTimeout(() => setActiveStep('instructions'), 350);
    }
  };
  const handleTextAdd = () => {
    if (copiedText.trim()) {
      if (remainingSlots <= 0) {
        showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
        return;
      }
      setCopiedTextConfirmed(true);
      simulateIndexing('text', 1800);
      // Auto-advance to instructions
      setTimeout(() => setActiveStep('instructions'), 350);
    }
  };
  // Determine if a source has been provided
  const hasSource = useMemo(() => {
    return !!(uploadedFiles.length > 0 || linkConfirmed || copiedTextConfirmed || importedWebSources.length > 0);
  }, [uploadedFiles, linkConfirmed, copiedTextConfirmed, importedWebSources]);
  // Source summaries for display (supports multiple sources)
  const sourceSummaries = useMemo(() => {
    const items: Array<{
      type: 'file' | 'link' | 'text' | 'web';
      label: string;
    }> = [];
    uploadedFiles.forEach(f => items.push({
      type: 'file',
      label: f.name
    }));
    if (linkConfirmed) items.push({
      type: 'link',
      label: linkUrl
    });
    if (copiedTextConfirmed) items.push({
      type: 'text',
      label: copiedText.slice(0, 60) + (copiedText.length > 60 ? '...' : '')
    });
    if (importedWebSources.length > 0) items.push({
      type: 'web',
      label: `${importedWebSources.length} web source${importedWebSources.length > 1 ? 's' : ''}`
    });
    return items;
  }, [uploadedFiles, linkConfirmed, linkUrl, copiedTextConfirmed, copiedText, importedWebSources]);
  // Validation logic
  const getValidationError = useCallback((): string => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      return 'Add instructions describing what you want to generate.';
    }
    if (trimmed.length < 20) {
      return 'Please provide more detail (at least 20 characters).';
    }
    if (activeStep === 'source' && activeSourceTab === 'link' && linkUrl.trim() && !linkConfirmed) {
      return 'Confirm your link or switch to instructions to continue.';
    }
    if (activeStep === 'source' && activeSourceTab === 'text' && copiedText.trim() && !copiedTextConfirmed) {
      return 'Confirm your copied text or switch to instructions to continue.';
    }
    return '';
  }, [prompt, activeStep, activeSourceTab, linkUrl, linkConfirmed, copiedText, copiedTextConfirmed]);
  const isCreateDisabled = !!getValidationError() || isRecording;
  // Settings modal: require a meaningful name + education level before generating
  const hasValidTitle = title.trim().length > 0 && title.trim() !== 'Untitled';
  const hasEducationLevel = selectedGroup !== null;
  const canGenerate = processingComplete && hasValidTitle && hasEducationLevel;
  const handleCreate = () => {
    // If on source step, nudge to instructions
    if (activeStep === 'source') {
      setActiveStep('instructions');
      return;
    }
    const error = getValidationError();
    if (error) {
      showValidation(error);
      return;
    }
    setValidationMessage('');
    // Default title based on mode
    setTitle(isJourney ? 'New Learning Journey' : 'New Assessment');
    // For assessments: show architect loading first
    // For journeys: keep existing processing flow
    if (isJourney) {
      setIsProcessing(true);
      setProcessingStage(0);
      setProcessingComplete(false);
    } else {
      setShowArchitectLoading(true);
    }
  };
  // Simulate processing stages
  const stageDurations = [2000, 3000, 4000, 0];
  useEffect(() => {
    if (!isProcessing || processingComplete) return;
    if (processingStage < stageDurations.length - 1) {
      processingTimerRef.current = setTimeout(() => {
        setProcessingStage(prev => prev + 1);
      }, stageDurations[processingStage]);
    } else {
      setProcessingComplete(true);
    }
    return () => {
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
    };
  }, [isProcessing, processingStage, processingComplete]);
  const handleTemplateSelect = (template: (typeof templates)[0]) => {
    if (selectedTemplate === template.id) {
      const currentTemplate = templates.find(t => t.id === template.id);
      if (currentTemplate && prompt === currentTemplate.prompt) {
        setPrompt('');
      }
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(template.id);
      setPrompt(template.prompt);
      // Auto-select card optimization for journey templates
      if ('cardOptimization' in template && template.cardOptimization) {
        setCardOptimization(template.cardOptimization);
      }
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const topicIndex = template.prompt.indexOf('Topic / Chapter: ') + 'Topic / Chapter: '.length;
          textareaRef.current.setSelectionRange(topicIndex, topicIndex);
          autoResizeTextarea();
        }
      }, 50);
    }
  };
  const handleModeSelect = (mode: 'assessment' | 'journey') => {
    setCreationMode(mode);
    // Reset template and prompt when switching modes
    setSelectedTemplate(null);
    setPrompt('');
  };
  const closeProcessing = useCallback(() => {
    setIsProcessing(false);
    setProcessingComplete(false);
    setProcessingStage(0);
    if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
  }, []);
  const handleChangeMode = () => {
    // Reset processing state FIRST to ensure mode selector cards reappear
    closeProcessing();
    // Reset wizard state
    setSelectedTemplate(null);
    setPrompt('');
    setActiveStep('source');
    setActiveSourceTab(null);
    // Reset source state
    setUploadedFiles([]);
    setLinkUrl('');
    setLinkConfirmed(false);
    setCopiedText('');
    setCopiedTextConfirmed(false);
    setImportedWebSources([]);
    setSearchQuery('');
    setValidationMessage('');
    setIsSearchModalOpen(false);
    setIsTextModalOpen(false);
    // Clear indexing state
    setIndexingSources(new Set());
    indexingTimersRef.current.forEach(timer => clearTimeout(timer));
    indexingTimersRef.current.clear();
    // Set creationMode to null LAST so all resets are applied before re-render
    setCreationMode(null);
  };
  // Unified mode accent color — keeps the entire wizard visually cohesive
  const accent = isJourney ? '#2563EB' : '#00B75F';
  const accentHover = isJourney ? '#1d4ed8' : '#00a055';
  // Dynamic placeholder text
  const getPlaceholderText = () => {
    if (isRecording) return 'Listening… your text will appear when done.';
    if (isJourney) {
      return hasSource ? 'Describe the learning journey to create from your source: topic focus, key concepts, difficulty progression, target audience…' : 'Describe the learning journey you want to create: topic, target audience, key concepts to cover, difficulty progression…';
    }
    return hasSource ? 'Describe what to generate from your source: topic focus, question types, difficulty, number of questions…' : 'Describe the assessment you want to create: topic, grade level, question types, difficulty…';
  };
  // CTA button text
  const getCtaText = () => {
    if (activeStep === 'source') {
      return <>
          Next: Write Instructions
          <ArrowRightIcon size={18} data-id="element-1041" />
        </>;
    }
    if (isJourney) {
      return <>
          <RouteIcon size={18} data-id="element-1042" />
          {hasSource ? 'Generate Journey from Source' : 'Create Learning Journey'}
        </>;
    }
    return <>
        <SparklesIcon size={18} data-id="element-1043" />
        {hasSource ? 'Generate from Source' : 'Create Assessment'}
      </>;
  };
  return <>
      <section className="w-full max-w-2xl mx-auto mb-12 sm:mb-20" data-id="element-1044">
        {/* Header */}
        <div className="text-center mb-5 sm:mb-8" data-id="element-1045">
          <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.4, 0, 0.2, 1]
        }} data-id="element-1046">
            <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-1.5 sm:mb-2" data-id="element-1047">
              What would you like to create?
            </h2>
            <p className="text-sm text-text-secondary max-w-md mx-auto" data-id="element-1048">
              Choose how to engage your students with your content.
            </p>
          </motion.div>
        </div>

        {/* ─── CREATION MODE SELECTOR / WIZARD ─── */}
        <AnimatePresence data-id="element-1049">
          {creationMode === null && !isProcessing && <motion.div key="mode-selector" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.1
          }
        }} transition={{
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1]
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6" data-id="element-1050">
              {/* Assessment Card */}
              <button onClick={() => handleModeSelect('assessment')} className="group relative flex flex-row sm:flex-col items-center sm:items-start text-left p-4 sm:p-6 rounded-xl border-2 border-custom bg-surface hover:border-[#00B75F]/50 hover:shadow-lg hover:shadow-[#00B75F]/8 active:scale-[0.98] transition-all duration-200 cursor-pointer min-h-[80px] sm:min-h-0 gap-4 sm:gap-0" aria-label="Create an Assessment" data-id="element-1051">
                <div className="w-12 h-12 sm:w-12 sm:h-12 rounded-xl bg-[#00B75F]/10 flex items-center justify-center flex-shrink-0 sm:mb-3.5 group-hover:bg-[#00B75F]/15 transition-colors" data-id="element-1052">
                  <ClipboardListIcon size={22} className="text-[#00B75F]" data-id="element-1053" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1054">
                  <h3 className="text-sm sm:text-base font-bold text-text-primary mb-0.5 sm:mb-1" data-id="element-1055">
                    Assessment
                  </h3>
                  <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1056">
                    Generate questions to test and evaluate knowledge
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-7 sm:h-7 rounded-full bg-surface-elevated border border-custom flex items-center justify-center flex-shrink-0 sm:absolute sm:top-5 sm:right-5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-1057">
                  <ArrowRightIcon size={14} className="text-[#00B75F]" data-id="element-1058" />
                </div>
              </button>

              {/* Learning Journey Card */}
              <button onClick={() => handleModeSelect('journey')} className="group relative flex flex-row sm:flex-col items-center sm:items-start text-left p-4 sm:p-6 rounded-xl border-2 border-custom bg-surface hover:border-[#2563EB]/50 hover:shadow-lg hover:shadow-[#2563EB]/8 active:scale-[0.98] transition-all duration-200 cursor-pointer min-h-[80px] sm:min-h-0 gap-4 sm:gap-0" aria-label="Create a Learning Journey" data-id="element-1059">
                {/* New Badge */}
                <span className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white bg-[#FF9D33] rounded-full" data-id="element-1060">
                  New
                </span>
                <div className="w-12 h-12 sm:w-12 sm:h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 sm:mb-3.5 group-hover:bg-[#2563EB]/15 transition-colors" data-id="element-1061">
                  <RouteIcon size={22} className="text-[#2563EB]" data-id="element-1062" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1063">
                  <h3 className="text-sm sm:text-base font-bold text-text-primary mb-0.5 sm:mb-1" data-id="element-1064">
                    Playbook
                  </h3>
                  <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1065">
                    A guided, visual path through topics with interactive
                    lessons
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-7 sm:h-7 rounded-full bg-surface-elevated border border-custom flex items-center justify-center flex-shrink-0 sm:absolute sm:bottom-5 sm:right-5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-1066">
                  <ArrowRightIcon size={14} className="text-[#2563EB]" data-id="element-1067" />
                </div>
              </button>
            </motion.div>}
        </AnimatePresence>

        <AnimatePresence data-id="element-1068">
          {/* No journey wizard modal */}

          {/* `journey` mode can be handled via AssessmentWorkspace with mode prop */}
        </AnimatePresence>
      </section>

      {/* ─── ASSESSMENT WORKSPACE ─── */}
      <AnimatePresence data-id="element-1069">
        {(creationMode === 'assessment' || creationMode === 'journey') && <AssessmentWorkspace mode={creationMode} onClose={handleChangeMode} onGenerate={(blueprint, settings) => {
        console.log('Generating with blueprint:', blueprint);
        // Generation is now handled by the loading overlay inside AssessmentWorkspace
      }} data-id="element-1070" />}
      </AnimatePresence>

      <WebSearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} initialQuery={searchQuery} importedSources={importedWebSources} onRemoveImported={id => {
      setImportedWebSources(prev => prev.filter(s => s.id !== id));
    }} onImport={sources => {
      setImportedWebSources(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const newSources = sources.filter(s => !existingIds.has(s.id));
        const currentTotal = uploadedFiles.length + (linkConfirmed ? 1 : 0) + (copiedTextConfirmed ? 1 : 0) + prev.length;
        const slotsAvailable = MAX_SOURCES - currentTotal;
        if (slotsAvailable <= 0) {
          showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
          return prev;
        }
        const trimmed = newSources.slice(0, slotsAvailable);
        if (newSources.length > slotsAvailable) {
          showValidation(`Only ${slotsAvailable} slot${slotsAvailable === 1 ? '' : 's'} remaining. Some sources were not imported.`);
        }
        // Simulate indexing for each accepted web source
        trimmed.forEach(s => simulateIndexing(s.id, 2000 + Math.random() * 1000));
        return [...prev, ...trimmed];
      });
      // Auto-advance to instructions
      setTimeout(() => setActiveStep('instructions'), 350);
    }} data-id="element-1071" />

      {/* Journey Planning Screen */}
      <JourneyPlanningScreen isOpen={showJourneyPlanning} onClose={() => setShowJourneyPlanning(false)} onConfirm={plan => {
      console.log('Journey plan confirmed:', plan);
      setShowJourneyPlanning(false);
      // Reset the entire wizard
      handleChangeMode();
    }} journeyTitle={title} sourceSummaries={sourceSummaries} data-id="element-1072" />

      {/* Copied Text Modal */}
      <AnimatePresence data-id="element-1073">
        {isTextModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-1074">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsTextModalOpen(false)} data-id="element-1075" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 12
        }} transition={{
          duration: 0.2
        }} className="relative w-full max-w-2xl bg-surface rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-1076">
              <div className="flex items-center justify-between px-6 py-4 border-b border-custom flex-shrink-0" data-id="element-1077">
                <div className="flex items-center gap-3" data-id="element-1078">
                  <div className={`w-9 h-9 rounded-xl bg-[${accent}]/10 flex items-center justify-center`} data-id="element-1079">
                    <ClipboardListIcon size={18} className={`text-[${accent}]`} data-id="element-1080" />
                  </div>
                  <div data-id="element-1081">
                    <h2 className="text-lg font-bold text-text-primary" data-id="element-1082">
                      Paste Text
                    </h2>
                    <p className="text-[11px] text-text-tertiary mt-0.5" data-id="element-1083">
                      Add raw text to use as a source
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsTextModalOpen(false)} className="p-2 -mr-2 rounded-lg hover:bg-surface-elevated text-text-tertiary hover:text-text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close text modal" data-id="element-1084">
                  <XIcon size={20} data-id="element-1085" />
                </button>
              </div>
              <div className="p-6" data-id="element-1086">
                <textarea value={copiedText} onChange={e => setCopiedText(e.target.value)} placeholder="Paste your text here..." className={`w-full h-64 p-4 bg-surface-elevated border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[${accent}] focus:border-transparent text-text-primary placeholder-text-tertiary resize-none`} autoFocus data-id="element-1087" />
              </div>
              <div className="px-6 py-4 border-t border-custom bg-surface flex items-center justify-end gap-3 flex-shrink-0" data-id="element-1088">
                <button onClick={() => setIsTextModalOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:bg-surface-elevated transition-colors" data-id="element-1089">
                  Cancel
                </button>
                <button onClick={() => {
              handleTextAdd();
              setIsTextModalOpen(false);
            }} disabled={!copiedText.trim()} className={`px-5 py-2 bg-[${accent}] text-white rounded-lg text-sm font-semibold hover:bg-[${accentHover}] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 transition-colors flex items-center gap-2`} data-id="element-1090">
                  <CheckCircleIcon size={16} data-id="element-1091" />
                  Confirm Text
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
};
export default CreateAssessmentOptions;